import React, { Fragment } from 'react'
import ManagerNavigation from './ManagerNavigation'
import UserNavigation from './UserNavigation'
import { Cog6ToothIcon } from '@heroicons/react/24/outline'
import { Link } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'
import { Menu, Transition } from '@headlessui/react'
import { clsx } from 'clsx'
import Gravatar from '../../../../components/common/components/Gravatar'

type SidebarNavigationProps = {
  logoutPath: string
  csrfToken: string
}

const userNavigation = [{ name: 'Your profile', href: '/profile' }]

const SidebarNavigation = ({ logoutPath, csrfToken }: SidebarNavigationProps) => {
  const { user } = useSelector((state: RootState) => state.app)

  const handleLogout = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()

    const form = document.createElement('form')
    form.method = 'POST'
    form.action = logoutPath

    const _methodInput = document.createElement('input')
    _methodInput.type = 'hidden'
    _methodInput.name = '_method'
    _methodInput.value = 'delete'
    form.appendChild(_methodInput)

    const csrfInput = document.createElement('input')
    csrfInput.type = 'hidden'
    csrfInput.name = 'authenticity_token'
    csrfInput.value = csrfToken
    form.appendChild(csrfInput)

    document.body.appendChild(form)
    form.submit()
  }

  return (
    <>
      <li>
        <UserNavigation />
      </li>
      <li>
        <ManagerNavigation
          title={'Manage'}
          allowedRoles={['admin', 'organization_admin']}
          menuItems={[
            {
              name: 'Data Sets',
              to: '/data_sets',
              initial: 'D',
              allowedRoles: ['admin', 'organization_manager'],
            },
            {
              name: 'My Team',
              to: '/my-team',
              initial: 'T',
              allowedRoles: ['admin', 'organization_manager'],
            },
            {
              name: 'My Library',
              to: '/my_library',
              initial: 'L',
              allowedRoles: ['admin', 'organization_manager'],
            },
            {
              name: 'Organization Library',
              to: '/organization_library',
              initial: 'O',
              allowedRoles: ['admin', 'organization_manager'],
            },
            {
              name: 'Credit Usage',
              to: '/credits',
              initial: 'C',
              allowedRoles: ['admin', 'organization_manager'],
            },
          ]}
        />
      </li>
      <li></li>
      <li className="-mx-6 mt-auto">
        <Menu as="div" className="relative outline-none">
          <Menu.Button className="flex items-center justify-between  w-full group px-6 py-3 text-sm font-semibold leading-6 text-gray-400 hover:bg-gray-800 cursor-pointer outline-none ">
            <div className="flex flex-row items-center gap-x-4">
              <Gravatar className="h-8 w-8 rounded-full" email={user.email} />
              <span aria-hidden="true">
                {user.firstName} {user.lastName}
              </span>
            </div>
            <Cog6ToothIcon
              className="ml-2 h-5 w-5 text-gray-400 group-hover:text-gray-200"
              aria-hidden="true"
            />
          </Menu.Button>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            leave="transition ease-in duration-75"
            enterFrom="transform opacity-0 scale-95 translateY(100%)"
            enterTo="transform opacity-100 scale-100"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95 translateY(100%)"
          >
            <Menu.Items className="absolute left-0 bottom-full z-10 mb-2.5 w-full outline-none">
              <div className="rounded-md bg-gray-50 py-2 mx-4 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
                {user.isOrganizationAdmin && (
                  <Menu.Item>
                    {({ active }) => (
                      <Link
                        to="/settings"
                        className={clsx(
                          active ? 'bg-gray-100' : '',
                          'block px-3 py-1 text-sm leading-6 text-gray-900'
                        )}
                      >
                        Settings
                      </Link>
                    )}
                  </Menu.Item>
                )}

                {userNavigation.map((item) => (
                  <Menu.Item key={item.name}>
                    {({ active }) => (
                      <Link
                        to={item.href}
                        className={clsx(
                          active ? 'bg-gray-100' : '',
                          'block px-3 py-1 text-sm leading-6 text-gray-900'
                        )}
                      >
                        {item.name}
                      </Link>
                    )}
                  </Menu.Item>
                ))}

                {user.roles.includes('admin') && (
                  <div className="border-t border-b border-gray-200">
                    <Menu.Item>
                      {() => (
                        <a
                          href="/admin"
                          className={clsx('block px-3 py-1 text-sm leading-6 text-gray-900')}
                        >
                          Rails Admin
                        </a>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {() => (
                        <a
                          href="/sidekiq"
                          className={clsx('block px-3 py-1 text-sm leading-6 text-gray-900')}
                        >
                          Sidekiq
                        </a>
                      )}
                    </Menu.Item>
                  </div>
                )}

                <Menu.Item>
                  {() => (
                    <a
                      href="#"
                      onClick={handleLogout}
                      className={`block px-3 py-1 text-sm text-gray-900 w-full text-left hover:bg-gray-100`}
                    >
                      Logout
                    </a>
                  )}
                </Menu.Item>
              </div>
            </Menu.Items>
          </Transition>
        </Menu>
      </li>
    </>
  )
}

export default SidebarNavigation
